import React, { useState, useEffect, Fragment } from "react";
import { toast } from "sonner";
import {
  Plus,
  Edit,
  Trash2,
  Search,
  RefreshCw,
  Building2,
  Users,
  ChevronLeft,
  ChevronRight,
  X,
  Check,
  AlertCircle,
  MoreVertical,
  Calendar,
  User,
  Filter
} from "lucide-react";
import { Dialog, Transition } from "@headlessui/react";
import {
  createDepartment,
  updateDepartment,
  deleteDepartment,
  listDepartmentsPaginated,
  getUserDepartmentStats,
  Department,
  PaginatedDepartmentsResponse
} from "../../../service/apiService";
import { useUser } from "../../../context/UserContext";

interface CreateEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  department?: Department | null;
  onSuccess: () => void;
  userId: string; 
}

const CreateEditModal: React.FC<CreateEditModalProps> = ({
  isOpen,
  onClose,
  department,
  onSuccess,
  userId 
}) => {
  const [departmentName, setDepartmentName] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (department) {
      setDepartmentName(department.department_name);
    } else {
      setDepartmentName("");
    }
  }, [department]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!departmentName.trim()) {
      toast.error("Department name is required");
      return;
    }

    try {
      setLoading(true);
      if (department) {
        await updateDepartment(department.id, { department_name: departmentName.trim() }); 
        toast.success("Department updated successfully");
      } else {
        await createDepartment(departmentName.trim()); 
        toast.success("Department created successfully");
      }
      onSuccess();
      onClose();
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || "An error occurred";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 scrim flex items-center justify-center z-50 p-4">
      <div className="bg-surface-overlay rounded-lg p-4 sm:p-6 w-full max-w-md mx-auto">
        <div className="flex justify-between items-center mb-4 sm:mb-6">
          <h3 className="text-lg sm:text-xl font-semibold">
            {department ? "Edit Department" : "Create Department"}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1 touch-manipulation"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4 sm:mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Department Name
            </label>
            <input
              type="text"
              value={departmentName}
              onChange={(e) => setDepartmentName(e.target.value)}
              className="w-full px-3 py-2.5 sm:py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent touch-manipulation"
              placeholder="Enter department name"
              required
              disabled={loading}
            />
          </div>

          <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="w-full sm:w-auto px-4 py-2.5 sm:py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none touch-manipulation"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="w-full sm:w-auto px-4 py-2.5 sm:py-2 text-sm font-medium text-white bg-violet-600 rounded-md hover:bg-violet-700 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center touch-manipulation"
            >
              {loading && <RefreshCw className="animate-spin mr-2" size={16} />}
              {department ? "Update" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface DeleteConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  department: Department | null;
  onConfirm: () => void;
  loading: boolean;
}

const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({
  isOpen,
  onClose,
  department,
  onConfirm,
  loading
}) => {
  if (!isOpen || !department) return null;

  return (
    <div className="fixed inset-0 scrim flex items-center justify-center z-50 p-4">
      <div className="bg-surface-overlay rounded-lg p-4 sm:p-6 w-full max-w-md mx-auto">
        <div className="flex items-center mb-4 sm:mb-6">
          <AlertCircle className="text-red-500 mr-3 flex-shrink-0" size={24} />
          <h3 className="text-lg sm:text-xl font-semibold">Delete Department</h3>
        </div>

        <p className="text-gray-600 mb-4 sm:mb-6 text-sm sm:text-base">
          Are you sure you want to delete the department "{department.department_name}"? 
          This action cannot be undone.
        </p>

        <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
          <button
            onClick={onClose}
            className="w-full sm:w-auto px-4 py-2.5 sm:py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none touch-manipulation"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="w-full sm:w-auto px-4 py-2.5 sm:py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center touch-manipulation"
          >
            {loading && <RefreshCw className="animate-spin mr-2" size={16} />}
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

// Mobile-friendly Department Card Component
interface DepartmentCardProps {
  department: Department;
  onEdit: (department: Department) => void;
  onDelete: (department: Department) => void;
}

const DepartmentCard: React.FC<DepartmentCardProps> = ({
  department,
  onEdit,
  onDelete
}) => {
  const [showActions, setShowActions] = useState(false);

  return (
    <div className="bg-surface-overlay border border-border-default rounded-lg p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-3 min-w-0 flex-1">
          <Building2 className="text-violet-600 mt-1 flex-shrink-0" size={20} />
          <div className="min-w-0 flex-1">
            <h3 className="text-base font-medium text-gray-900 truncate">
              {department.department_name}
            </h3>
            <div className="mt-2 space-y-1">
              <div className="flex items-center text-sm text-gray-600">
                <User className="mr-2 flex-shrink-0" size={14} />
                <span className="truncate">{department.created_by}</span>
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <Calendar className="mr-2 flex-shrink-0" size={14} />
                <span>{new Date(department.created_at).toLocaleDateString()}</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="relative ml-2">
          <button
            onClick={() => setShowActions(!showActions)}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 touch-manipulation"
          >
            <MoreVertical size={16} />
          </button>
          
          {showActions && (
            <>
              <div 
                className="fixed inset-0 z-10" 
                onClick={() => setShowActions(false)}
              />
              <div className="absolute right-0 top-full mt-1 w-48 bg-surface-overlay rounded-md shadow-lg border border-border-default z-20">
                <button
                  onClick={() => {
                    onEdit(department);
                    setShowActions(false);
                  }}
                  className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                >
                  <Edit size={16} className="mr-3" />
                  Edit Department
                </button>
                <button
                  onClick={() => {
                    onDelete(department);
                    setShowActions(false);
                  }}
                  className="w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-red-50 flex items-center"
                >
                  <Trash2 size={16} className="mr-3" />
                  Delete Department
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const DepartmentManagement: React.FC = () => {
  const { userDetails, userId } = useUser(); 
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage] = useState(10);
  const [filterType, setFilterType] = useState<'my_departments' | 'all'>('all');
  const [viewMode, setViewMode] = useState<'table' | 'cards'>('table');
  
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [tempFilter, setTempFilter] = useState<'my_departments' | 'all'>('all');
  
  const [isCreateEditModalOpen, setIsCreateEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState<Department | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Auto-switch to card view on mobile
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setViewMode('cards');
      } else {
        setViewMode('table');
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchDepartments = async () => {
    if (!userId) {
      console.log("No userId available, skipping fetch");
      return;
    }

    try {
      setLoading(true);
      const response: PaginatedDepartmentsResponse = await listDepartmentsPaginated(
        currentPage,
        itemsPerPage,
        filterType
      );
      
      setDepartments(response.departments);
      setTotalPages(response.pagination.total_pages);
      setTotalItems(response.pagination.total_items);
    } catch (error: any) {
      console.error("Error fetching departments:", error);
      const errorMessage = error.response?.data?.detail || "Failed to fetch departments";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userDetails?.role === 'admin' && userId) { 
      fetchDepartments();
    }
  }, [userDetails, userId, currentPage, filterType]); 

  const filteredDepartments = departments.filter(dept =>
    dept.department_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Filter modal handlers
  const handleFilterClick = () => {
    setTempFilter(filterType);
    setShowFilterModal(true);
  };

  const handleApplyFilter = async () => {
  if (tempFilter !== filterType) {
    setShowFilterModal(false);
    setFilterType(tempFilter);
    setSearchQuery(""); 
  } else {
    setShowFilterModal(false);
  }
};

  const handleClearFilters = async () => {
  const defaultFilter: 'my_departments' | 'all' = 'all';
  if (filterType !== defaultFilter) {
    setShowFilterModal(false);
    setTempFilter(defaultFilter);
    setFilterType(defaultFilter);
    setSearchQuery("");
  } else {
    setTempFilter(defaultFilter);
    setShowFilterModal(false);
  }
};

  const getFilterDisplayText = () => {
    switch (filterType) {
      case 'my_departments':
        return 'My Departments';
      case 'all':
        return 'All Departments';
      default:
        return 'All Departments';
    }
  };

  const handleCreateEdit = (department?: Department) => {
    setSelectedDepartment(department || null);
    setIsCreateEditModalOpen(true);
  };

  const handleDelete = (department: Department) => {
    setSelectedDepartment(department);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedDepartment || !userId) return;

    try {
      setDeleteLoading(true);
      await deleteDepartment(selectedDepartment.id); 
      toast.success("Department deleted successfully");
      setIsDeleteModalOpen(false);
      setSelectedDepartment(null);
      fetchDepartments();
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || "Failed to delete department";
      toast.error(errorMessage);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleModalSuccess = () => {
    fetchDepartments();
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  if (userDetails?.role !== 'admin') {
    return (
      <div className="flex items-center justify-center h-64 px-4">
        <p className="text-gray-500 text-center text-sm sm:text-base">
          Department management is only available for admin users.
        </p>
      </div>
    );
  }

  if (!userId) {
    return (
      <div className="flex items-center justify-center h-64 px-4">
        <p className="text-gray-500 text-center text-sm sm:text-base">
          Loading user information...
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="p-4 sm:p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 space-y-4 sm:space-y-0">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Department Management</h2>
            <p className="text-gray-600 mt-1 text-sm sm:text-base">Manage organizational departments and assignments</p>
          </div>
          <button
            onClick={() => handleCreateEdit()}
            className="w-full sm:w-auto bg-violet-600 hover:bg-violet-700 text-white px-4 py-2.5 sm:py-2 rounded-lg flex items-center justify-center sm:justify-start space-x-2 font-medium touch-manipulation"
          >
            <Plus size={20} />
            <span>Create Department</span>
          </button>
        </div>

        {/*Filters and Search */}
        <div className="bg-surface-overlay rounded-lg shadow-sm border border-border-default p-4 mb-4 sm:mb-6">
          <div className="flex flex-col space-y-4 sm:space-y-0 sm:flex-row sm:justify-between sm:items-center">
            <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
              {/* Filter Button */}
              <button
                onClick={handleFilterClick}
                disabled={loading}
                className={`flex items-center justify-between px-3 py-2 text-sm border border-gray-200 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-violet-500 min-w-[140px] ${
                  loading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-violet-600 mr-2"></div>
                    <span>Loading...</span>
                  </div>
                ) : (
                  <>
                   <span>{getFilterDisplayText()}</span>
                   <Filter size={14} className="ml-2 text-gray-400" />
                 </>
                )}
            </button>
            </div>

            <div className="relative w-full sm:w-auto sm:max-w-xs">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Search departments..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent touch-manipulation"
              />
            </div>
          </div>
        </div>

        {/* Departments List */}
        <div className="bg-surface-overlay rounded-lg shadow-sm border border-border-default">
          {loading ? (
            <div className="flex justify-center items-center p-8">
              <RefreshCw className="animate-spin text-violet-600" size={24} />
              <span className="ml-2 text-gray-600 text-sm sm:text-base">
                Loading departments...
              </span>
            </div>
          ) : filteredDepartments.length === 0 ? (
            <div className="text-center p-6 sm:p-8">
              <Building2 className="mx-auto text-gray-400 mb-4" size={40} />
              <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">
                {searchQuery ? "No departments found" : "No departments yet"}
              </h3>
              <p className="text-gray-600 mb-4 text-sm sm:text-base px-4">
                {searchQuery 
                  ? "Try adjusting your search criteria"
                  : "Create your first department to get started"
                }
              </p>
              {!searchQuery && (
                <button
                  onClick={() => handleCreateEdit()}
                  className="bg-violet-600 hover:bg-violet-700 text-white px-4 py-2 rounded-lg text-sm sm:text-base touch-manipulation"
                >
                  Create Department
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Mobile Card View */}
              <div className="block md:hidden">
                <div className="p-4 space-y-4">
                  {filteredDepartments.map((department) => (
                    <DepartmentCard
                      key={department.id}
                      department={department}
                      onEdit={handleCreateEdit}
                      onDelete={handleDelete}
                    />
                  ))}
                </div>
              </div>

              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Department Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created By
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created At
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-surface-overlay divide-y divide-border-default">
                    {filteredDepartments.map((department) => (
                      <tr key={department.id} className="hover:bg-surface-2">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Building2 className="text-violet-600 mr-3" size={20} />
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {department.department_name}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{department.created_by}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {new Date(department.created_at).toLocaleDateString()}
                          </div>
                          <div className="text-sm text-gray-500">
                            {new Date(department.created_at).toLocaleTimeString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={() => handleCreateEdit(department)}
                              className="text-muted hover:text-primary hover:bg-surface-2 p-1 rounded touch-manipulation transition-colors"
                              title="Edit Department"
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              onClick={() => handleDelete(department)}
                              className="text-red-600 hover:text-red-900 p-1 rounded touch-manipulation"
                              title="Delete Department"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="bg-surface-overlay px-4 py-3 border-t border-border-default sm:px-6">
                  <div className="flex items-center justify-between">
                    {/* Mobile Pagination */}
                    <div className="flex-1 flex justify-between md:hidden">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                      >
                        Previous
                      </button>
                      <span className="text-sm text-gray-700 px-4 py-2">
                        {currentPage} of {totalPages}
                      </span>
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                      >
                        Next
                      </button>
                    </div>
                    
                    {/* Desktop Pagination */}
                    <div className="hidden md:flex md:flex-1 md:items-center md:justify-between">
                      <div>
                        <p className="text-sm text-gray-700">
                          Showing{" "}
                          <span className="font-medium">
                            {(currentPage - 1) * itemsPerPage + 1}
                          </span>{" "}
                          to{" "}
                          <span className="font-medium">
                            {Math.min(currentPage * itemsPerPage, totalItems)}
                          </span>{" "}
                          of <span className="font-medium">{totalItems}</span> results
                        </p>
                      </div>
                      <div>
                        <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                          <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <ChevronLeft size={16} />
                          </button>
                          {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                            let page: number;
                            if (totalPages <= 5) {
                              page = i + 1;
                            } else if (currentPage <= 3) {
                              page = i + 1;
                            } else if (currentPage >= totalPages - 2) {
                              page = totalPages - 4 + i;
                            } else {
                              page = currentPage - 2 + i;
                            }
                            
                            return (
                              <button
                                key={page}
                                onClick={() => handlePageChange(page)}
                                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                  page === currentPage
                                    ? "z-10 bg-violet-50 border-violet-500 text-violet-600"
                                    : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                                }`}
                              >
                                {page}
                              </button>
                            );
                          })}
                          <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <ChevronRight size={16} />
                          </button>
                        </nav>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Modals */}
        <CreateEditModal
          isOpen={isCreateEditModalOpen}
          onClose={() => setIsCreateEditModalOpen(false)}
          department={selectedDepartment}
          onSuccess={handleModalSuccess}
          userId={userId} 
        />

        <DeleteConfirmModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          department={selectedDepartment}
          onConfirm={confirmDelete}
          loading={deleteLoading}
        />
      </div>

      {/* Filter Modal */}
      <Transition appear show={showFilterModal} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowFilterModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-surface-overlay p-6 text-left align-middle shadow-xl transition-all">
                  <div className="flex justify-between items-center mb-4">
                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                      Filter Departments
                    </Dialog.Title>
                    <button
                      onClick={() => setShowFilterModal(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X size={20} />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <h4 className="flex items-center text-sm font-medium text-gray-700 mb-3">
                        <Building2 size={16} className="mr-2 text-violet-600" />
                        Department Filters
                      </h4>
                      <div className="space-y-2">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="filter"
                            value="all"
                            checked={tempFilter === 'all'}
                            onChange={(e) => setTempFilter(e.target.value as 'my_departments' | 'all')}
                            className="h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300"
                          />
                          <span className="ml-2 text-sm text-gray-700">All Departments</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="filter"
                            value="my_departments"
                            checked={tempFilter === 'my_departments'}
                            onChange={(e) => setTempFilter(e.target.value as 'my_departments' | 'all')}
                            className="h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300"
                          />
                          <span className="ml-2 text-sm text-gray-700">My Departments</span>
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 flex justify-between">
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-transparent bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2"
                      onClick={handleClearFilters}
                    >
                      Clear all filters
                    </button>
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md border border-transparent bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2"
                      onClick={handleApplyFilter}
                    >
                      Done
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default DepartmentManagement;